import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { actionBack } from '@ibk/shared-assets';
import { useTranslation } from '@ibk/shared-translation';
import { Alert, Button, Icon, Loading } from '@ibk/shared-ui';
import { useBenefitContract } from '../../hooks/useBenefitContract';
import { useRecharge } from '../../hooks/useRecharge';
import { useRechargeReport } from '../../hooks/useRechargeReport';
import { getInvoiceState } from '../../utils';
import { buildRechargeReportFilename, resolveOrderIdentifier } from '../../utils/fileNamingUtils';
import { BeneficiariesList } from './components/BeneficiariesList';
import { InvoiceAlert } from './components/InvoiceAlert';
import { PaymentInfo } from './components/PaymentInfo';
import { ProductInfo } from './components/ProductInfo';
import styles from './RechargeDetailsModal.module.css';
const noop = () => undefined;
export function RechargeDetailsModal({ isOpen, rechargeId, contractId, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [alert, setAlert] = useState({ visible: false });
    const [showInvoiceAlert, setShowInvoiceAlert] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const { recharge, isLoading, isFetching, isError, refetch, updateRecharge, isUpdating, isUpdateSuccess, isUpdateError, updateError, resetUpdate = noop, cancelRecharge, isCanceling, isCancelSuccess, isCancelError, resetCancel = noop, } = useRecharge({
        voucherContractId: contractId,
        rechargeId: rechargeId ?? undefined,
        enabled: isOpen && !!rechargeId,
    });
    const { contractData } = useBenefitContract();
    const { isError: isReportError, isLoading: isReportLoading, fetchReport, reset: resetReport = noop, } = useRechargeReport();
    const resettersRef = useRef({ resetUpdate, resetCancel, resetReport });
    resettersRef.current = { resetUpdate, resetCancel, resetReport };
    useEffect(() => {
        setAlert({
            visible: false,
        });
        setShowInvoiceAlert(false);
        setShowCopySuccess(false);
        resettersRef.current.resetUpdate();
        resettersRef.current.resetCancel();
        resettersRef.current.resetReport();
    }, [isOpen, rechargeId]);
    useEffect(() => {
        if (isReportError) {
            setAlert({
                visible: true,
                message: t('benefits.recharge-details.report-error'),
                type: 'error',
            });
        }
    }, [isReportError, t]);
    useEffect(() => {
        if (isUpdateError) {
            const errorResult = updateError?.details?.result;
            const isCutoffError = errorResult === 'CONTRACT_RECHARGE_CREDIT_DATE_TODAY_AFTER_CUTOFF';
            setAlert({
                visible: true,
                message: isCutoffError
                    ? t('benefits.recharge-details.error.update-credit-release-cutoff')
                    : t('benefits.recharge-details.error.update-credit-release'),
                type: 'error',
            });
        }
    }, [isUpdateError, updateError, t]);
    useEffect(() => {
        if (isUpdateSuccess) {
            setAlert({
                visible: true,
                message: t('benefits.recharge-details.success.update-credit-release'),
                type: 'success',
            });
        }
    }, [isUpdateSuccess, t]);
    useEffect(() => {
        if (isCancelSuccess) {
            setIsCancelModalOpen(false);
            setAlert({
                visible: true,
                message: t('benefits.recharge-details.success.cancel-recharge'),
                type: 'success',
            });
        }
    }, [isCancelSuccess, t]);
    useEffect(() => {
        if (isCancelError) {
            setAlert({
                visible: true,
                message: t('benefits.recharge-details.error.cancel-recharge'),
                type: 'error',
            });
        }
    }, [isCancelError, t]);
    if (!isOpen) {
        return null;
    }
    const handleCopyProtocol = async () => {
        if (recharge?.recharge?.orderId) {
            try {
                await navigator.clipboard.writeText(recharge.recharge.orderId);
                setShowCopySuccess(true);
            }
            catch {
                setShowCopySuccess(false);
            }
        }
    };
    const handleMakePayment = () => {
        navigate(`/beneficios/recarga/${rechargeId}/pagar`, {
            replace: true,
            state: {
                rechargeId,
            },
        });
    };
    const handleDownloadReport = async () => {
        if (!rechargeId || !contractId) {
            return;
        }
        const rechargeDetail = recharge?.recharge;
        const filename = buildRechargeReportFilename(contractData?.companyName ?? '', rechargeDetail?.accrualPeriod ?? '', resolveOrderIdentifier(rechargeDetail?.name, rechargeDetail?.orderId ?? ''));
        fetchReport({
            voucherContractId: contractId,
            voucherContractRechargeId: rechargeId,
        }, filename);
    };
    const handleReleaseCredit = (date) => {
        if (date) {
            updateRecharge({ creditDate: date });
        }
    };
    const handleCancelOrder = () => {
        setIsCancelModalOpen(!isCancelModalOpen);
    };
    const handleConfirmCancelOrder = async () => {
        try {
            await cancelRecharge();
        }
        catch (error) {
        }
    };
    const renderHeader = () => {
        return (_jsxs(Modal.Header, { className: styles.modalHeader, children: [_jsx("button", { type: 'button', onClick: onClose, className: styles.backButton, "aria-label": t('back'), "data-testid": 'button-recharge-details-modal-back', children: _jsx("img", { src: actionBack, alt: '' }) }), _jsx(Modal.Title, { className: styles.modalTitle, children: t('benefits.recharge-details.title') })] }));
    };
    const renderBody = () => {
        if (isLoading || isFetching) {
            return (_jsxs("section", { className: styles.feedbackContainer, children: [_jsx("div", { className: styles.loadingIconContent, children: _jsx(Loading, { size: 'small' }) }), _jsx("h3", { className: styles.feedbackTitle, children: t('benefits.recharge-details.loading.title') }), _jsx("p", { className: styles.feedbackText, children: t('benefits.recharge-details.loading.description') })] }));
        }
        if (isError || !recharge?.recharge) {
            return (_jsxs("section", { className: styles.feedbackContainer, children: [_jsx(Icon, { name: 'circleError', className: styles.errorIcon }), _jsx("h3", { className: styles.feedbackTitle, children: t('benefits.recharge-details.error.title') }), _jsx("p", { className: styles.feedbackText, children: t('benefits.recharge-details.error.description') }), _jsx(Button, { variant: 'primary', size: 'small', onClick: refetch, className: styles.feedbackButton, dataTestId: 'recharge-details-retry', children: t('benefits.recharge-details.error.retry') })] }));
        }
        const rechargeDetail = recharge.recharge;
        const invoiceState = getInvoiceState(rechargeDetail.rechargeState, recharge.invoices);
        return (_jsxs(Modal.Body, { className: styles.modalBody, children: [showCopySuccess && (_jsx(Alert, { className: styles.alert, message: t('benefits.recharge-details.success.copy-protocol'), appearance: 'success', alertType: 'inline', density: 'compact', alignItems: 'center', duration: 4, hideCloseButton: false, onClose: () => setShowCopySuccess(false), testIdIdentifier: 'CopyProtocolSuccess' })), alert?.visible && (_jsx(Alert, { className: styles.alert, message: alert.message ?? '', appearance: alert.type ?? 'error', alertType: 'inline', density: 'compact', alignItems: 'center', hideCloseButton: false, onClose: () => {
                        setAlert({ visible: false });
                    } })), _jsx(InvoiceAlert, { status: invoiceState.status, releaseState: rechargeDetail.releaseState, isVisible: showInvoiceAlert }), _jsx(ProductInfo, { rechargeData: rechargeDetail, onCopyProtocol: handleCopyProtocol, isReportLoading: isReportLoading, onDownloadReport: handleDownloadReport, isReleaseCreditLoading: isUpdating, onReleaseCredit: handleReleaseCredit }), _jsx(PaymentInfo, { rechargeData: rechargeDetail, invoices: recharge.invoices, onMakePayment: handleMakePayment, onShowInvoiceAlert: () => setShowInvoiceAlert(true), invoiceState: invoiceState, onCancelOrder: handleCancelOrder, onConfirmCancelOrder: handleConfirmCancelOrder, isCancelModalOpen: isCancelModalOpen, isCancelLoading: isCanceling }), _jsx(BeneficiariesList, { rechargeData: rechargeDetail })] }));
    };
    return (_jsxs(Modal, { show: isOpen, onHide: onClose, dialogClassName: styles.modalDialog, contentClassName: styles.modalContent, className: styles.modal, backdropClassName: styles.backdrop, "data-testid": 'modalRechargeDetailsModalContainer', children: [renderHeader(), renderBody()] }));
}
